import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiMessageSquare, FiSend, FiUser, FiPhone,
  FiMail, FiSearch, FiFilter, FiMoreVertical, FiClock,
  FiCheck, FiCheckCircle, FiAlertCircle, FiRefreshCw
} from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import { BsWhatsapp, BsRobot } from 'react-icons/bs';
import toast from 'react-hot-toast';
import api from '../utils/api';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  status?: 'sending' | 'sent' | 'delivered' | 'read';
}

interface Conversation {
  id: string;
  customer: {
    name: string;
    avatar?: string;
    phone?: string;
    email?: string;
  };
  assistant: {
    name: string;
    color: string;
  };
  channel: 'sms' | 'whatsapp' | 'webchat' | 'phone' | 'email';
  status: 'active' | 'resolved' | 'waiting';
  lastMessage: string;
  lastActivity: Date;
  unreadCount: number;
  messages: Message[];
}

const Conversations: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fetch conversations from API
    const fetchConversations = async () => {
      try {
        const response = await api.get('/conversations');
        const data = response.data || [];
        setConversations(data);
        if (data.length > 0) {
          setSelectedConversation(data[0]);
        }
      } catch (error) {
        console.error('Failed to fetch conversations:', error);
      }
    };
    fetchConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const channelIcons = {
    sms: <FiMessageSquare size={16} className="text-blue-500" />,
    whatsapp: <BsWhatsapp size={16} className="text-green-500" />,
    webchat: <FiMessageSquare size={16} className="text-purple-500" />,
    phone: <FiPhone size={16} className="text-orange-500" />,
    email: <FiMail size={16} className="text-red-500" />
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-500';
      case 'waiting': return 'text-yellow-500';
      case 'resolved': return 'text-gray-500';
      default: return 'text-gray-500';
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || conv.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const sendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const newMsg: Message = {
      id: Date.now().toString(),
      content: newMessage,
      role: 'user',
      timestamp: new Date(),
      status: 'sending'
    };

    // Add message to conversation
    const updatedConversation = {
      ...selectedConversation,
      messages: [...selectedConversation.messages, newMsg],
      lastMessage: newMessage,
      lastActivity: new Date()
    };

    setSelectedConversation(updatedConversation);
    setConversations(convs => 
      convs.map(c => c.id === selectedConversation.id ? updatedConversation : c)
    );
    setNewMessage('');

    // Simulate bot typing
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: 'Thank you for your message. I\'m processing your request...',
        role: 'assistant',
        timestamp: new Date()
      };

      const finalConversation = {
        ...updatedConversation,
        messages: [...updatedConversation.messages, botResponse],
        lastMessage: botResponse.content,
        lastActivity: new Date()
      };

      setSelectedConversation(finalConversation);
      setConversations(convs => 
        convs.map(c => c.id === selectedConversation.id ? finalConversation : c)
      );
    }, 2000);
  };

  const formatTime = (dateInput: Date | string) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.05, 0.15, 0.05],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          >
            <FiMessageSquare size={30 + i * 5} className="text-blue-500/10" />
          </motion.div>
        ))}
      </div>

      {/* Conversations List */}
      <div className="w-80 relative z-10" style={{
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-2xl font-bold text-white mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Conversations
          </h2>
          
          {/* Search */}
          <div className="relative mb-4">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            {['all', 'active', 'waiting', 'resolved'].map((status) => (
              <motion.button
                key={status}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1 rounded-lg text-xs capitalize transition-all ${
                  filterStatus === status
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {status}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.map((conversation) => (
            <motion.div
              key={conversation.id}
              whileHover={{ x: 5 }}
              onClick={() => setSelectedConversation(conversation)}
              className={`p-4 cursor-pointer border-b border-gray-800 transition-colors ${
                selectedConversation?.id === conversation.id
                  ? 'bg-purple-500/20 border-purple-500/50'
                  : 'hover:bg-gray-800/50'
              }`}
            >
              <div className="flex items-start gap-3">
                <motion.div
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold"
                  animate={selectedConversation?.id === conversation.id ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  {conversation.customer.name.charAt(0)}
                </motion.div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-white truncate">
                      {conversation.customer.name}
                    </h4>
                    <div className="flex items-center gap-1">
                      {channelIcons[conversation.channel]}
                      <span className="text-xs text-gray-400">
                        {formatTime(conversation.lastActivity)}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-400 truncate mb-1">
                    {conversation.lastMessage}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${getStatusColor(conversation.status)} capitalize`}>
                      {conversation.status}
                    </span>
                    {conversation.unreadCount > 0 && (
                      <motion.span
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className="bg-purple-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                      >
                        {conversation.unreadCount}
                      </motion.span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col relative z-10">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-800" style={{
              background: 'rgba(30, 41, 59, 0.8)',
              backdropFilter: 'blur(20px)',
            }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white"
                  >
                    {selectedConversation.customer.name.charAt(0)}
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-white">
                      {selectedConversation.customer.name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      {channelIcons[selectedConversation.channel]}
                      <span>{selectedConversation.channel}</span>
                      <span>•</span>
                      <span className={getStatusColor(selectedConversation.status)}>
                        {selectedConversation.status}
                      </span>
                    </div>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700"
                >
                  <FiMoreVertical size={20} />
                </motion.button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <AnimatePresence>
                {selectedConversation.messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-xs lg:max-w-md xl:max-w-lg ${
                      message.role === 'user' ? 'order-2' : 'order-1'
                    }`}>
                      <div className={`px-4 py-2 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                          : 'bg-gray-800 text-gray-100'
                      }`}>
                        <p className="text-sm">{message.content}</p>
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}>
                        <span>{message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {message.role === 'user' && (
                          <span className="text-purple-400">
                            {message.status === 'sending' ? <FiClock size={12} /> : 
                             message.status === 'sent' ? <FiCheck size={12} /> : 
                             <FiCheckCircle size={12} />}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      message.role === 'user' ? 'order-1 ml-2' : 'order-2 mr-2'
                    }`}>
                      {message.role === 'user' ? (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xs">
                          {selectedConversation.customer.name.charAt(0)}
                        </div>
                      ) : (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                          className={`w-8 h-8 rounded-full bg-gradient-to-br ${selectedConversation.assistant.color} flex items-center justify-center`}
                        >
                          <BiBot size={16} color="white" />
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Typing Indicator */}
              <AnimatePresence>
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="flex justify-start"
                  >
                    <div className="flex items-center gap-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        className={`w-8 h-8 rounded-full bg-gradient-to-br ${selectedConversation.assistant.color} flex items-center justify-center`}
                      >
                        <BiBot size={16} color="white" />
                      </motion.div>
                      <div className="bg-gray-800 px-4 py-2 rounded-2xl">
                        <div className="flex gap-1">
                          {[0, 1, 2].map((i) => (
                            <motion.div
                              key={i}
                              animate={{ scale: [1, 1.5, 1] }}
                              transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                              className="w-2 h-2 bg-gray-400 rounded-full"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-800" style={{
              background: 'rgba(30, 41, 59, 0.8)',
              backdropFilter: 'blur(20px)',
            }}>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiSend size={20} />
                </motion.button>
              </div>
            </div>
          </>
        ) : (
          /* No Conversation Selected */
          <div className="flex-1 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-8"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className="inline-block mb-4"
              >
                <BsRobot size={64} className="text-purple-400" />
              </motion.div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Select a Conversation
              </h3>
              <p className="text-gray-400">
                Choose a conversation from the left to start chatting
              </p>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Conversations;